//region imports
import auth from "../util/auth";
import http from "../util/http";
import cryptoUtil from "../util/crypto";
//endregion imports

const generateId = () => {
    return Math.random().toString(36).substring(2, 10) + Date.now();
};

const VISITOR_KEY = "visitor_id";
const SESSION_KEY = "session_id";
const SESSION_EXPIRY_MS = 30 * 60 * 1000;

class Analytics {
    static _instances = {};
    static DESTROY_WHITELIST = ["OrderCart", "OrderDetail"];
    static _storeToken = '';
    static _shoppingAreaCode = '';
    static _stallId = 0;

    _route = null;
    _page = "";
    _fromPage = "";
    _observer = null;
    _bounceTimer = null;
    _hasInteracted = false;
    _bounced = false;
    _pendingViews = {};
    _allowTracking = false;
    _startTime = 0;
    _enterTime = 0;
    _sentLeaveEvent = false;

    constructor(currentRoute, page, fromPage = null) {
        this._route = currentRoute;
        this._page = page;
        if (fromPage) {
            this._fromPage = fromPage;
        }
    }

    //region static
    static handleStoreToken(to) {
        const isOuterPage = to.path.split('/').filter(Boolean).length === 1;

        if (isOuterPage) {
            const { id, storeId } = to.query;

            if (id || storeId) {
                this.setStoreToken({ id, storeId });
            } else {
                this.clearStoreToken();
            }
        }
    }

    static handleShoppingArea({path, stallId}) {
        let code = path.split('/ev/')[1]?.split('/')[0] || '';
        if (path.includes('/ev/') && code !== '') {
            this.setShoppingAreaCode(code);
        } else {
            this.clearShoppingAreaCode();
        }
        this.setStallId(stallId);
    }

    static setStoreToken({ id, storeId }) {
        const raw = id || storeId;
        this._storeToken = raw ? cryptoUtil.encryptText(`${raw};`) : null;
    }

    static setShoppingAreaCode(code) {
        this._shoppingAreaCode = code;
    }

    static setStallId(id) {
        this._stallId = id;
    }

    static getStoreToken() {
        return this._storeToken;
    }

    static getShoppingAreaCode() {
        return this._shoppingAreaCode;
    }

    static getStallId() {
        return this._stallId;
    }

    static clearShoppingAreaCode() {
        this._shoppingAreaCode = '';
    }

    static clearStoreToken() {
        this._storeToken = '';
    }

    static getInstance(route, page, fromPage = null) {
        if (!page) throw new Error("Analytics.getInstance 需要傳入 page 名稱");

        if (!Analytics._instances[page]) {
            const instance = new Analytics(route, page, fromPage);
            Analytics._instances[page] = instance;
            instance.trackPage();
        } else {
            const instance = Analytics._instances[page];
            instance.setRoute(route);
            instance.setPage(page);
        }

        return Analytics._instances[page];
    }

    static getInstanceByPage(page) {
        return Analytics._instances[page] ?? null;
    }

    static destroyInstance(page, currentRouteName) {
        if (Analytics.DESTROY_WHITELIST.includes(currentRouteName)) return;

        const instance = Analytics._instances[page];
        if (!instance) return;

        instance._destroyViewTracker();
        instance._resetBounce();

        document.removeEventListener("click", instance._markInteractedOnce);
        document.removeEventListener("keydown", instance._markInteractedOnce);
        document.removeEventListener("scroll", instance._markInteractedOnce);
        document.removeEventListener("wheel", instance._markInteractedOnce);
        document.removeEventListener("touchstart", instance._markInteractedOnce);
        window.removeEventListener("beforeunload", instance._handleBeforeUnload);
        document.removeEventListener("visibilitychange", instance._handleVisibilityChange);

        delete Analytics._instances[page];
    }

    static get EVENTS() {
        return {
            VISIT_PAGE: 1,
            BOUNCE: 2,
            VIEW_ITEM: 3,
            ADD_TO_CART: 4,
            REMOVE_FROM_CART: 5,
            CLEAR_CART: 6,
            SUBMIT_ORDER: 7,
            LEAVE_PAGE: 8,
            JOIN_MEMBER: 9,
            MAKE_A_CALL: 10,
            QUERY_ADDRESS: 11,
        };
    }
    //endregion static

    //region public
    trackPage() {
        this._startTime = Date.now();
        this._enterTime = Date.now();
        this._sentLeaveEvent = false;
        this.sendEvent(Analytics.EVENTS.VISIT_PAGE, {fromPageName: this._fromPage});
        this._initBounceDetection();
    }

    initViewTracker({ elements, getFoodId, delay = 2000, threshold = 0.7 }) {
        if (!elements || elements.length === 0) return;

        this._observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const el = entry.target;
                    const foodId = getFoodId(el);
                    if (!foodId) return;

                    if (entry.isIntersecting) {
                        if (!this._pendingViews[foodId]) {
                            this._scheduleViewTracking(foodId, delay);
                        }
                    } else {
                        if (this._pendingViews[foodId]) {
                            clearTimeout(this._pendingViews[foodId].timer);
                            delete this._pendingViews[foodId];
                        }
                    }
                });
            },
            { threshold }
        );

        elements.forEach((el) => {
            if (el) {
                this._observer.observe(el);

                const rect = el.getBoundingClientRect();
                const isVisible =
                    rect.top < window.innerHeight &&
                    rect.bottom > 0 &&
                    rect.left < window.innerWidth &&
                    rect.right > 0;

                if (isVisible) {
                    const foodId = getFoodId(el);
                    if (foodId && !this._pendingViews[foodId]) {
                        this._scheduleViewTracking(foodId, delay);
                    }
                }
            }
        });
    }

    sendEvent(event, metadata = {}) {
        if (event === Analytics.EVENTS.LEAVE_PAGE) {
            if (this._sentLeaveEvent || Analytics.DESTROY_WHITELIST.includes(this._route?.name)) return;
            this._sentLeaveEvent = true;
        }

        const itemId = metadata?.foodId ?? metadata?.itemId;
        const code = metadata?.code ?? '';
        const page = this._page ?? this._route?.name;
        if ((!itemId && code === '') && event === Analytics.EVENTS.VIEW_ITEM) return;

        const payload = {
            logEventId: event,
            page,
            itemId: isNaN(Number(itemId)) ? 0 : Number(itemId),
            visitorId: this.getVisitorId(),
            sessionId: this.getSessionId(),
            userToken: auth.getAdminToken(),
            storeToken: auth.getStoreToken() || Analytics.getStoreToken(),
            os: http.getOS(),
            userAgent: navigator.userAgent,
            metadata: JSON.stringify(metadata),
            shoppingAreaCode: Analytics.getShoppingAreaCode(),
            code: code,
            stallId: Analytics.getStallId(),
        };

        http.fetchWithEncrypt`SendAnalyticsEvent${payload}
        ${json => {
            if (!json.status) {
                // console.warn("Analytics event failed", payload);
            }
        }}
        ${() => {}}
    `;
    }

    getLeavePageMetadata() {
        return {
            interaction: this._hasInteracted,
            totalStaySeconds: Math.floor((Date.now() - this._enterTime) / 1000),
            visibilityState: document.visibilityState,
        };
    }

    recordViewed(foodId, viewReason = "unknown") {
        this.sendEvent(Analytics.EVENTS.VIEW_ITEM, {
            foodId,
            viewReason,
            triggeredBy: viewReason.toLowerCase().includes("clicked") ? "userClick" : "viewDetection",
            viewedDurationMs: viewReason === "delayedViewed" ? 2000 : undefined,
        });
    }

    setRoute(route) {
        this._route = route;
    }

    setPage(page) {
        this._page = page;
    }
    //endregion public

    //region private
    _scheduleViewTracking(foodId, delay) {
        const timer = setTimeout(() => {
            if (this._hasInteracted) {
                this.recordViewed(Number(foodId), "delayedViewed");
                delete this._pendingViews[foodId];
            } else {
                this._pendingViews[foodId].ready = true;
            }
        }, delay);

        this._pendingViews[foodId] = { timer, ready: false };
    }

    _initBounceDetection() {
        this._hasInteracted = false;
        this._bounced = false;
        this._allowTracking = false;

        const getBounceMetadata = (reason = "timeout") => ({
            interaction: false,
            timeUntilBounce: this._startTime
                ? Math.floor((Date.now() - this._startTime) / 1000)
                : 0,
            visibilityState: document.visibilityState,
            reason,
        });

        this._bounceTimer = setTimeout(() => {
            if (!this._hasInteracted && !this._bounced) {
                this._bounced = true;
                this.sendEvent(Analytics.EVENTS.BOUNCE, getBounceMetadata("timeout"));
            } else {
                this._allowTracking = true;
            }
        }, 10000);

        const markInteracted = () => {
            this._hasInteracted = true;
            this._allowTracking = true;
            this._startTime = Date.now();
            clearTimeout(this._bounceTimer);

            Object.entries(this._pendingViews).forEach(([foodId, entry]) => {
                if (entry.ready) {
                    this.recordViewed(Number(foodId), "delayedViewed");
                    clearTimeout(entry.timer);
                    delete this._pendingViews[foodId];
                }
            });
        };

        const sendLeaveEvent = () => {
            this.sendEvent(Analytics.EVENTS.LEAVE_PAGE, this.getLeavePageMetadata());

            if (!this._hasInteracted && !this._bounced && !Analytics.DESTROY_WHITELIST.includes(this._route?.name)) {
                this._bounced = true;
                this.sendEvent(Analytics.EVENTS.BOUNCE, getBounceMetadata("earlyExit"));
            }
        };

        this._handleBeforeUnload = () => sendLeaveEvent();
        this._handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                sendLeaveEvent();
            }
        };

        this._markInteractedOnce = markInteracted;

        document.addEventListener("click", this._markInteractedOnce, { once: true });
        document.addEventListener("keydown", this._markInteractedOnce, { once: true });
        document.addEventListener("scroll", this._markInteractedOnce, { once: true, passive: true });
        document.addEventListener("wheel", this._markInteractedOnce, { once: true, passive: true });
        document.addEventListener("touchstart", this._markInteractedOnce, { once: true, passive: true });

        window.addEventListener("beforeunload", this._handleBeforeUnload);
        document.addEventListener("visibilitychange", this._handleVisibilityChange);
    }

    _resetBounce() {
        clearTimeout(this._bounceTimer);
        this._bounceTimer = null;
        this._hasInteracted = false;
        this._bounced = false;
        this._allowTracking = false;
    }

    _destroyViewTracker() {
        this._observer?.disconnect();
        this._observer = null;

        Object.values(this._pendingViews).forEach(({ timer }) => {
            clearTimeout(timer);
        });

        this._pendingViews = {};
    }

    getVisitorId() {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = generateId();
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    }

    getSessionId() {
        const session = JSON.parse(localStorage.getItem(SESSION_KEY));
        const now = Date.now();

        if (session && now - session.timestamp < SESSION_EXPIRY_MS) {
            return session.id;
        }

        const newSession = {
            id: generateId(),
            timestamp: now,
        };
        localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
        return newSession.id;
    }
    //endregion private
}

export default Analytics;
